import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Editor from "@monaco-editor/react";
import CustomDeployPanel from "../components/custom/CustomDeployPanel";
import { api } from "../lib/api";
import { compileCustomContract } from "../lib/customContractsApi";
import {
    loadDraft,
    saveDraft,
    deleteDraft,
    setActiveDraft,
    getActiveDraftId,
    clearActiveDraft,
} from "../lib/customDraftStorage";
import { getCurrentAddress } from "../wallet/walletClient";
import { useAuth } from "../auth/useAuth";
import { buildCustomBuilderSeed } from "../lib/customSolidityTemplates";
import CustomDeploymentHistory from "../components/custom/CustomDeploymentHistory";

function renderSafeText(value, fallback = "-") {
    if (value === null || value === undefined || value === "") return fallback;

    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (typeof value === "bigint") return value.toString();

    if (Array.isArray(value)) {
        if (value.length === 0) return fallback;
        return value
            .map((item) => renderSafeText(item, ""))
            .filter(Boolean)
            .join(", ");
    }

    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return fallback;
        }
    }

    return fallback;
}

function safeError(err, fallback = "Something went wrong.") {
    const value =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        fallback;

    return renderSafeText(value, fallback);
}

function detectPrimaryContractName(source) {
    const text = String(source || "");

    const matches = Array.from(
        text.matchAll(/\bcontract\s+([A-Za-z_][A-Za-z0-9_]*)\b/g),
    );

    if (matches.length === 0) return "";

    return matches[0]?.[1] || "";
}

function configureSolidity(monaco) {
    const alreadyRegistered = monaco.languages
        .getLanguages()
        .some((lang) => lang.id === "solidity");

    if (!alreadyRegistered) {
        monaco.languages.register({ id: "solidity" });
    }

    monaco.languages.setMonarchTokensProvider("solidity", {
        tokenizer: {
            root: [
                [/\/\/.*$/, "comment"],
                [/\/\*/, "comment", "@comment"],
                [/"([^"\\]|\\.)*$/, "string.invalid"],
                [/"/, "string", "@string"],
                [
                    /\b(contract|interface|library|function|constructor|modifier|event|error|struct|enum|mapping|returns|return|if|else|for|while|do|break|continue|emit|import|pragma|is|using|new|try|catch|revert|require|assert)\b/,
                    "keyword",
                ],
                [
                    /\b(public|private|external|internal|view|pure|payable|memory|storage|calldata|virtual|override|immutable|constant|indexed)\b/,
                    "type",
                ],
                [
                    /\b(address|bool|string|bytes|bytes32|uint|uint256|uint96|int|int256)\b/,
                    "type.identifier",
                ],
                [/[a-zA-Z_$][\w$]*/, "identifier"],
                [/[{}()[\]]/, "@brackets"],
                [/[;,.]/, "delimiter"],
                [/[0-9]+/, "number"],
            ],
            comment: [
                [/[^/*]+/, "comment"],
                [/\*\//, "comment", "@pop"],
                [/[/*]/, "comment"],
            ],
            string: [
                [/[^\\"]+/, "string"],
                [/\\./, "string.escape"],
                [/"/, "string", "@pop"],
            ],
        },
    });
}

function WarningList({ warnings }) {
    if (!Array.isArray(warnings) || warnings.length === 0) {
        return (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                No custom security warnings returned.
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {warnings.map((warning, index) => {
                const isBlock = Boolean(warning?.blockDeploy);

                return (
                    <div
                        key={warning?.code || warning?.title || `warning-${index}`}
                        className={`rounded-xl border p-4 text-sm ${isBlock
                            ? "border-red-200 bg-red-50 text-red-800"
                            : "border-amber-200 bg-amber-50 text-amber-900"
                            }`}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="font-semibold">
                                {renderSafeText(warning?.title || warning?.code, "Warning")}
                            </div>
                            <div className="rounded-full bg-white/70 px-2 py-1 text-xs uppercase">
                                {renderSafeText(warning?.severity, "info")}
                            </div>
                        </div>

                        <p className="mt-2">
                            {renderSafeText(warning?.message, "No details provided.")}
                        </p>

                        {warning?.action ? (
                            <p className="mt-2 text-xs opacity-80">
                                Fix: {renderSafeText(warning.action)}
                            </p>
                        ) : null}

                        {isBlock ? (
                            <p className="mt-2 text-xs font-semibold">
                                Deployment blocked until fixed.
                            </p>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function JsonBox({ title, value }) {
    if (!value) return null;

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-sm font-semibold text-slate-800">{title}</div>
            <pre className="max-h-72 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-100">
                {JSON.stringify(value, null, 2)}
            </pre>
        </div>
    );
}

function getCompileDisplayType(result) {
    return (
        result?.displayType ||
        result?.capabilities?.displayType ||
        "Custom Contract"
    );
}

function getCompileStarterTemplate(result, fallback) {
    return (
        result?.starterTemplate ||
        result?.capabilities?.starterTemplate ||
        fallback ||
        "CUSTOM"
    );
}

function getCompileCapabilityLabels(result) {
    const labels =
        result?.capabilityLabels ||
        result?.capabilities?.capabilityLabels ||
        [];

    return Array.isArray(labels) ? labels.filter(Boolean) : [];
}

function CustomSoliditySupportNotice() {
    return (
        <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-900">
            <div className="font-semibold">Custom Solidity support</div>
            <p className="mt-1">
                Krionex deploys the final Solidity code you compile. The starter
                template is only a starting point. If you edit the contract
                structure, Krionex detects the final contract type after compile.
            </p>

            <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-blue-100 bg-white/70 p-3">
                    <div className="font-semibold">Supported now</div>
                    <p className="mt-1 text-xs leading-5">
                        Compile, preflight, wallet-signed deployment, verification,
                        history, explorer links, ERC20/ERC721/ERC1155 detection,
                        Ownable detection, and AccessControl detection.
                    </p>
                </div>

                <div className="rounded-xl border border-blue-100 bg-white/70 p-3">
                    <div className="font-semibold">Not included yet</div>
                    <p className="mt-1 text-xs leading-5">
                        Specialized dashboards for staking, vesting, escrow, DAO,
                        marketplace, airdrop, game, factory, or every custom business
                        function. Those contracts can be deployed and verified, but
                        full management UIs come later.
                    </p>
                </div>
            </div>
        </div>
    );
}

function buildStoredCompileResult(result) {
    if (!result?.customVersionId && !result?.versionId) return null;

    return {
        ok: Boolean(result.ok),
        restoredFromDraft: true,

        projectId: result.projectId || "",
        customVersionId: result.customVersionId || result.versionId || "",
        versionId: result.versionId || result.customVersionId || "",

        contractName: result.contractName || "",
        sourceName: result.sourceName || "",
        tokenType: result.tokenType || "",
        starterTemplate: result.starterTemplate || "",
        detectedContractType: result.detectedContractType || "",
        displayType: result.displayType || "",
        isToken: Boolean(result.isToken),
        capabilityLabels: Array.isArray(result.capabilityLabels)
            ? result.capabilityLabels
            : [],

        compilerVersion: result.compilerVersion || "",
        solcVersion: result.solcVersion || "",

        capabilities: result.capabilities || null,
        warnings: Array.isArray(result.warnings) ? result.warnings : [],
        deployBlocked: Boolean(result.deployBlocked),

        sourceHash: result.sourceHash || "",
        abiHash: result.abiHash || "",
        bytecodeHash: result.bytecodeHash || "",
        deployedBytecodeHash: result.deployedBytecodeHash || "",

        compiledAt: result.compiledAt || new Date().toISOString(),
    };
}

function toSafeNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function normalizeLimit(value) {
    if (value === "Unlimited" || value === "unlimited" || value === "Custom") {
        return -1;
    }

    const n = Number(value);
    if (!Number.isFinite(n)) return null;

    return n;
}

function getCustomUsageSnapshot(accountUsage) {
    const usage = accountUsage?.usage || accountUsage || {};
    const limits = accountUsage?.limits || {};

    const plan = String(
        accountUsage?.plan ||
        accountUsage?.planCode ||
        accountUsage?.billing?.planCode ||
        accountUsage?.subscription?.plan ||
        "free",
    )
        .trim()
        .toLowerCase();

    const role = String(
        accountUsage?.role ||
        accountUsage?.accountRole ||
        accountUsage?.user?.role ||
        "",
    )
        .trim()
        .toUpperCase();

    const privileged =
        accountUsage?.isPrivileged === true ||
        accountUsage?.isAdmin === true ||
        ["ADMIN", "SUPER_ADMIN", "OWNER"].includes(role);

    const deploymentsUsed = toSafeNumber(
        usage.deploymentsUsed ??
        usage.deployments ??
        usage.deploymentsCount ??
        accountUsage?.deploymentsUsed ??
        accountUsage?.deploymentsCount ??
        0,
        0,
    );

    const preflightsUsed = toSafeNumber(
        usage.forkSimulationsUsed ??
        usage.forkSimulations ??
        usage.forkSimulationCount ??
        accountUsage?.forkSimulationsUsed ??
        accountUsage?.forkSimulationCount ??
        0,
        0,
    );

    const deploymentsLimit = privileged
        ? -1
        : normalizeLimit(
            limits.deployments ??
            accountUsage?.deploymentsLimit ??
            accountUsage?.deploymentLimit,
        );

    const preflightsLimit = privileged
        ? -1
        : normalizeLimit(
            limits.forkSimulations ??
            limits.preflights ??
            accountUsage?.forkSimulationLimit ??
            accountUsage?.preflightLimit,
        );

    const deploymentLimitReached =
        deploymentsLimit != null &&
        deploymentsLimit >= 0 &&
        deploymentsUsed >= deploymentsLimit;

    const preflightLimitReached =
        preflightsLimit != null &&
        preflightsLimit >= 0 &&
        preflightsUsed >= preflightsLimit;

    return {
        plan,
        privileged,

        deploymentsUsed,
        deploymentsLimit,
        deploymentLimitReached,
        deploymentsText:
            deploymentsLimit == null
                ? `${deploymentsUsed} used`
                : deploymentsLimit < 0
                    ? `${deploymentsUsed} / Unlimited`
                    : `${deploymentsUsed} / ${deploymentsLimit}`,

        preflightsUsed,
        preflightsLimit,
        preflightLimitReached,
        preflightsText:
            preflightsLimit == null
                ? `${preflightsUsed} used`
                : preflightsLimit < 0
                    ? `${preflightsUsed} / Unlimited`
                    : `${preflightsUsed} / ${preflightsLimit}`,
    };
}

function getDeploymentLimitMessage(usageSnapshot) {
    return `You have reached your deployment limit. Your ${usageSnapshot.plan} plan allows ${usageSnapshot.deploymentsLimit} total standard or custom deployments. Upgrade to compile and deploy more contracts.`;
}

function getPreflightLimitMessage(usageSnapshot) {
    return `You have reached your preflight simulation limit. Your ${usageSnapshot.plan} plan allows ${usageSnapshot.preflightsLimit} total standard or custom preflight simulations.`;
}

export default function CustomBuilder() {
    const location = useLocation();
    const { isAuthed, restoreLoading } = useAuth();

    const walletAddress = getCurrentAddress?.() || "anonymous";

    const starterSeed = useMemo(() => {
        const params = new URLSearchParams(location.search || "");
        const routeProjectId =
            params.get("projectId") ||
            location.state?.customSeed?.projectId ||
            "";

        const seed = buildCustomBuilderSeed({
            search: location.search,
            state: location.state,
        });

        const activeProjectId = routeProjectId
            ? ""
            : getActiveDraftId(walletAddress);

        return {
            ...seed,
            projectId: routeProjectId || activeProjectId || seed.projectId,
        };
    }, [location.search, location.state, walletAddress]);

    const [projectId, setProjectId] = useState(starterSeed.projectId);
    const [projectName, setProjectName] = useState(starterSeed.projectName);
    const [contractName, setContractName] = useState(starterSeed.contractName);
    const [tokenType] = useState(starterSeed.tokenType);
    const [optimizerEnabled, setOptimizerEnabled] = useState(true);
    const [optimizerRuns, setOptimizerRuns] = useState("200");
    const [source, setSource] = useState(starterSeed.source);

    const [compileLoading, setCompileLoading] = useState(false);
    const [compileError, setCompileError] = useState("");
    const [compileResult, setCompileResult] = useState(null);
    const [draftStatus, setDraftStatus] = useState("");
    const [deploymentCompleted, setDeploymentCompleted] = useState(false);
    const [lastDeploymentResult, setLastDeploymentResult] = useState(null);

    const [accountUsage, setAccountUsage] = useState(null);
    const [accountUsageLoading, setAccountUsageLoading] = useState(false);
    const [accountUsageError, setAccountUsageError] = useState("");

    const deployBlocked = Boolean(compileResult?.deployBlocked);
    const detectedContractName = useMemo(() => {
        return detectPrimaryContractName(source);
    }, [source]);

    const contractNameMismatch = Boolean(
        detectedContractName &&
        contractName &&
        detectedContractName !== contractName,
    );

    const sourceSize = useMemo(() => {
        return new Blob([source || ""]).size;
    }, [source]);

    const loadAccountUsage = useCallback(async () => {
        if (restoreLoading) return;

        if (!isAuthed) {
            setAccountUsage(null);
            setAccountUsageError("");
            return;
        }

        setAccountUsageLoading(true);
        setAccountUsageError("");

        try {
            const res = await api.get("/account/usage", {
                headers: { "Cache-Control": "no-store" },
            });

            setAccountUsage(res.data || null);
        } catch (err) {
            setAccountUsage(null);
            setAccountUsageError(safeError(err, "Usage limits could not be loaded."));
        } finally {
            setAccountUsageLoading(false);
        }
    }, [isAuthed, restoreLoading]);

    useEffect(() => {
        loadAccountUsage();
    }, [loadAccountUsage]);

    const customUsage = useMemo(() => {
        return getCustomUsageSnapshot(accountUsage);
    }, [accountUsage]);

    const canCompile =
        isAuthed &&
        !restoreLoading &&
        !compileLoading &&
        !accountUsageLoading &&
        !customUsage.deploymentLimitReached;

    useEffect(() => {
        if (projectId) {
            setActiveDraft(walletAddress, projectId);
        }
    }, [walletAddress, projectId]);

    useEffect(() => {
        const draft = loadDraft(walletAddress, projectId);

        if (!draft) return;

        if (draft.projectName) setProjectName(draft.projectName);
        if (draft.contractName) setContractName(draft.contractName);
        if (draft.source) setSource(draft.source);
        if (draft.optimizerRuns) setOptimizerRuns(String(draft.optimizerRuns));

        if (typeof draft.optimizerEnabled === "boolean") {
            setOptimizerEnabled(draft.optimizerEnabled);
        }

        if (draft.lastDeploymentResult?.contractAddress) {
            setDeploymentCompleted(true);
            setLastDeploymentResult(draft.lastDeploymentResult);
            setCompileResult(null);
            setDraftStatus("Previous custom deployment completed. Start a new custom draft from Studio or compile again.");
            return;
        }

        if (draft.lastCompileResult?.customVersionId || draft.lastCompileResult?.versionId) {
            setDeploymentCompleted(false);
            setCompileResult({
                ...draft.lastCompileResult,
                restoredFromDraft: true,
            });
        }
    }, [walletAddress, projectId]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            saveDraft(walletAddress, projectId, {
                projectName,
                contractName,
                tokenType,
                source,
                optimizerEnabled,
                optimizerRuns,
                lastCompileResult:
                    !deploymentCompleted && compileResult
                        ? buildStoredCompileResult(compileResult)
                        : null,
                lastDeploymentResult: deploymentCompleted ? lastDeploymentResult : null,
            });

            setDraftStatus("Draft saved locally");
        }, 700);

        return () => window.clearTimeout(timer);
    }, [
        walletAddress,
        projectId,
        projectName,
        contractName,
        tokenType,
        source,
        optimizerEnabled,
        optimizerRuns,
        compileResult,
        deploymentCompleted,
        lastDeploymentResult,
    ]);

    async function handleCompile() {
        if (restoreLoading) {
            setCompileError("Checking your session. Please wait a moment.");
            return;
        }

        if (!isAuthed) {
            setCompileError(
                "Please connect wallet and sign in from the top bar before compiling.",
            );
            return;
        }

        if (accountUsageLoading) {
            setCompileError("Checking your usage limits. Please wait a moment.");
            return;
        }

        if (customUsage.deploymentLimitReached) {
            setCompileError(getDeploymentLimitMessage(customUsage));
            return;
        }

        const finalContractName = detectedContractName || contractName;

        if (!finalContractName) {
            setCompileError("No Solidity contract declaration found in source.");
            return;
        }

        if (detectedContractName && detectedContractName !== contractName) {
            setContractName(detectedContractName);
            setDraftStatus(`Contract name updated from Solidity source: ${detectedContractName}`);
        }

        setCompileLoading(true);
        setCompileError("");
        setCompileResult(null);
        setDeploymentCompleted(false);
        setLastDeploymentResult(null);

        try {
            const result = await compileCustomContract({
                projectId,
                projectName,
                contractName: finalContractName,
                tokenType,
                optimizerEnabled,
                optimizerRuns: Number(optimizerRuns || 200),
                source,
            });

            setCompileResult(result);

            saveDraft(walletAddress, projectId, {
                projectName,
                contractName: finalContractName,
                tokenType,
                source,
                optimizerEnabled,
                optimizerRuns,
                lastCompileResult: buildStoredCompileResult(result),
            });
        } catch (err) {
            setCompileError(safeError(err, "Custom compile failed."));
        } finally {
            setCompileLoading(false);
        }
    }

    function handleResetDraft() {
        deleteDraft(walletAddress, projectId);
        clearActiveDraft(walletAddress);

        setProjectName(starterSeed.projectName);
        setContractName(starterSeed.contractName);
        setSource(starterSeed.source);

        setCompileResult(null);
        setCompileError("");
        setDeploymentCompleted(false);
        setLastDeploymentResult(null);
        setDraftStatus("Draft reset from Studio selections");
    }

    return (
        <div className="mx-auto max-w-7xl px-4 py-8">
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <div className="flex flex-wrap items-center gap-3">
                            <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">
                                Krionex Custom Builder
                            </p>

                            <Link
                                to="/studio"
                                className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-black"
                            >
                                <span aria-hidden="true">←</span>
                                <span>Back to Studio</span>
                            </Link>
                        </div>

                        <h1 className="mt-1 text-3xl font-bold text-slate-950">
                            Custom Solidity Editor
                        </h1>
                        <p className="mt-2 max-w-3xl text-sm text-slate-600">
                            Start from a Studio template, edit Solidity, compile safely on the
                            backend, review warnings, then deploy only compiled versions. The
                            final deployed contract type is detected from the compiled ABI, not
                            only from the starter template.
                        </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-600">
                        <div>Wallet: {walletAddress || "Not detected"}</div>
                        <div>Starter template: {starterSeed.tokenType}</div>
                        <div>
                            Modules:{" "}
                            {starterSeed.modules?.length
                                ? starterSeed.modules.join(", ")
                                : "base"}
                        </div>
                        <div>Source size: {sourceSize.toLocaleString()} bytes</div>

                        {isAuthed ? (
                            <>
                                <div className="mt-2 border-t border-slate-200 pt-2">
                                    Plan:{" "}
                                    <span className="font-semibold capitalize">
                                        {customUsage.privileged ? "admin" : customUsage.plan}
                                    </span>
                                </div>
                                <div>
                                    Deployments:{" "}
                                    <span className="font-semibold">{customUsage.deploymentsText}</span>
                                </div>
                                <div>
                                    Preflights:{" "}
                                    <span className="font-semibold">{customUsage.preflightsText}</span>
                                </div>
                                <div className="mt-1 text-[11px] leading-4 text-slate-500">
                                    Standard and custom deployments share the same allowance.
                                </div>
                            </>
                        ) : null}

                        {accountUsageError ? (
                            <div className="mt-2 text-red-600">{accountUsageError}</div>
                        ) : null}

                        <div className="mt-2">{draftStatus}</div>
                    </div>
                </div>

                {!isAuthed ? (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-800">
                        Connect wallet and sign in from the top bar before compiling. You
                        can still view and edit the source safely.
                    </div>
                ) : null}
                <CustomSoliditySupportNotice />
            </div>

            <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
                <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-950">
                            Compile Settings
                        </h2>

                        <label className="mt-4 block text-sm font-medium text-slate-700">
                            Project ID
                        </label>
                        <input
                            value={projectId}
                            onChange={(e) => setProjectId(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />

                        <label className="mt-4 block text-sm font-medium text-slate-700">
                            Project Name
                        </label>
                        <input
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />

                        <label className="mt-4 block text-sm font-medium text-slate-700">
                            Contract Name
                        </label>
                        <input
                            value={contractName}
                            onChange={(e) => setContractName(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        />
                        {detectedContractName ? (
                            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                                Detected Solidity contract:{" "}
                                <span className="font-semibold">{detectedContractName}</span>

                                {contractNameMismatch ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setContractName(detectedContractName);
                                            setCompileError("");
                                            setDraftStatus("Contract name updated from Solidity source.");
                                        }}
                                        className="ml-2 font-semibold text-blue-700 hover:text-blue-900"
                                    >
                                        Use detected name
                                    </button>
                                ) : null}
                            </div>
                        ) : null}

                        {contractNameMismatch ? (
                            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                                Contract Name must match the Solidity contract declaration before compiling.
                            </div>
                        ) : null}

                        <label className="mt-4 block text-sm font-medium text-slate-700">
                            Started From Template
                        </label>

                        <div className="mt-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
                            {tokenType}
                        </div>

                        <p className="mt-1 text-xs leading-5 text-slate-500">
                            This only shows which template you started from. It does not define
                            the final deployed contract type. Krionex detects the real contract
                            type from the compiled Solidity after you click Compile.
                        </p>

                        <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <summary className="cursor-pointer text-sm font-semibold text-slate-800">
                                Advanced compiler settings
                            </summary>

                            <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={optimizerEnabled}
                                    onChange={(e) => setOptimizerEnabled(e.target.checked)}
                                />
                                Optimizer enabled
                            </label>

                            <label className="mt-4 block text-sm font-medium text-slate-700">
                                Optimizer Runs
                            </label>
                            <input
                                value={optimizerRuns}
                                onChange={(e) => setOptimizerRuns(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                            />

                            <p className="mt-2 text-xs leading-5 text-slate-500">
                                Default optimizer runs are suitable for most deployments. Change only
                                if you understand Solidity compiler optimization trade-offs.
                            </p>
                        </details>

                        {isAuthed && customUsage.deploymentLimitReached ? (
                            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                <div className="font-semibold">Deployment limit reached</div>
                                <div className="mt-1">
                                    {getDeploymentLimitMessage(customUsage)}{" "}
                                    <Link to="/upgrade" className="font-semibold underline underline-offset-4">
                                        View plans
                                    </Link>
                                </div>
                            </div>
                        ) : null}

                        {isAuthed && customUsage.preflightLimitReached ? (
                            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                <div className="font-semibold">Preflight limit reached</div>
                                <div className="mt-1">
                                    {getPreflightLimitMessage(customUsage)}{" "}
                                    <Link to="/upgrade" className="font-semibold underline underline-offset-4">
                                        View plans
                                    </Link>
                                </div>
                            </div>
                        ) : null}

                        <button
                            type="button"
                            onClick={handleCompile}
                            disabled={!canCompile}
                            className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {!isAuthed
                                ? "Connect Wallet in Header to Compile"
                                : accountUsageLoading
                                    ? "Checking Usage..."
                                    : customUsage.deploymentLimitReached
                                        ? "Deployment Limit Reached"
                                        : compileLoading
                                            ? "Compiling..."
                                            : "Compile Custom Solidity"}
                        </button>

                        <button
                            type="button"
                            onClick={handleResetDraft}
                            className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
                        >
                            Reset Draft
                        </button>

                        {compileError ? (
                            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                {compileError}
                            </div>
                        ) : null}
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-950">
                            Security Warnings
                        </h2>

                        <div className="mt-4">
                            {compileResult ? (
                                <WarningList warnings={compileResult?.warnings} />
                            ) : (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                                    Compile the contract to view backend security warnings.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-950">
                                    Solidity Source
                                </h2>
                                <p className="text-xs text-slate-500">
                                    Solidity 0.8.24, OpenZeppelin imports only
                                </p>
                            </div>
                        </div>

                        <div className="h-[680px]">
                            <Editor
                                height="100%"
                                language="solidity"
                                theme="vs-dark"
                                value={source}
                                beforeMount={configureSolidity}
                                onChange={(value) => {
                                    setSource(value || "");
                                    setCompileResult(null);
                                    setDeploymentCompleted(false);
                                    setLastDeploymentResult(null);
                                    setCompileError("");
                                    setDraftStatus("Source changed. Compile again before deploy.");
                                }}
                                options={{
                                    minimap: { enabled: true },
                                    fontSize: 14,
                                    lineNumbers: "on",
                                    wordWrap: "on",
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    tabSize: 4,
                                    insertSpaces: true,
                                    formatOnPaste: false,
                                    formatOnType: false,
                                }}
                            />
                        </div>
                    </div>

                    {compileResult ? (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-950">
                                        Compile Result
                                    </h2>
                                    <p className="text-sm text-slate-600">
                                        Version: {renderSafeText(compileResult.customVersionId)}
                                    </p>
                                </div>

                                <div
                                    className={`rounded-full px-3 py-1 text-xs font-semibold ${deployBlocked
                                        ? "bg-red-100 text-red-700"
                                        : "bg-emerald-100 text-emerald-700"
                                        }`}
                                >
                                    {deployBlocked ? "Deploy Blocked" : "Deploy Allowed"}
                                </div>
                            </div>

                            <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-5">
                                <div className="rounded-xl bg-slate-50 p-3">
                                    <div className="text-xs text-slate-500">Contract</div>
                                    <div className="font-semibold text-slate-900">
                                        {renderSafeText(compileResult.contractName)}
                                    </div>
                                </div>

                                <div className="rounded-xl bg-slate-50 p-3">
                                    <div className="text-xs text-slate-500">Started From </div>
                                    <div className="font-semibold text-slate-900">
                                        {renderSafeText(getCompileStarterTemplate(compileResult, tokenType))}
                                    </div>
                                </div>

                                <div className="rounded-xl bg-slate-50 p-3">
                                    <div className="text-xs text-slate-500">Detected Type</div>
                                    <div className="font-semibold text-slate-900">
                                        {renderSafeText(getCompileDisplayType(compileResult))}
                                    </div>
                                </div>

                                <div className="rounded-xl bg-slate-50 p-3">
                                    <div className="text-xs text-slate-500">Project ID</div>
                                    <div className="break-all font-semibold text-slate-900">
                                        {renderSafeText(compileResult.projectId)}
                                    </div>
                                </div>

                                <div className="rounded-xl bg-slate-50 p-3">
                                    <div className="text-xs text-slate-500">ABI Hash</div>
                                    <div className="break-all text-xs font-semibold text-slate-900">
                                        {renderSafeText(compileResult.abiHash)}
                                    </div>
                                </div>
                            </div>
                            {getCompileCapabilityLabels(compileResult).length ? (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {getCompileCapabilityLabels(compileResult).map((label) => (
                                        <span
                                            key={label}
                                            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {compileResult ? (
                        <CustomDeployPanel
                            compileResult={compileResult}
                            projectId={projectId}
                            tokenType={tokenType}
                            starterSeed={starterSeed}
                            source={source}
                            isAuthed={isAuthed}
                            restoreLoading={restoreLoading}
                            usageSnapshot={customUsage}
                            accountUsageLoading={accountUsageLoading}
                            onUsageRefresh={loadAccountUsage}
                            onDeploymentConfirmed={(result) => {
                                clearActiveDraft(walletAddress);
                                setDeploymentCompleted(true);
                                setLastDeploymentResult({
                                    contractAddress: result?.contractAddress || result?.address || "",
                                    address: result?.address || result?.contractAddress || "",
                                    network: result?.network || "",
                                    chainId: result?.chainId || null,
                                    txHash: result?.txHash || "",
                                    explorerUrl: result?.explorerUrl || "",
                                    explorerTxUrl: result?.explorerTxUrl || "",
                                    deploymentFile: result?.deploymentFile || "",
                                    deployedAt: new Date().toISOString(),
                                });
                                setDraftStatus("Custom deployment completed. This draft will not auto-restore as active.");
                                loadAccountUsage();
                            }}
                        />
                    ) : null}

                    <CustomDeploymentHistory isAuthed={isAuthed} />

                    <JsonBox title="Capabilities" value={compileResult?.capabilities} />
                    <JsonBox title="Full Compile Response" value={compileResult} />
                </div>
            </div>
        </div>
    );
}